/**
 * lib/billing/engines/west-bengal.ts — WBSEDCL Billing Engine
 * =============================================================
 * Builds a BillBreakdown from telemetry rows using WBSEDCL B-IDIT rules.
 * Receives the tariff config as a parameter — no direct tariff imports.
 *
 * Formula reference: VIREON-CORTEX Variables & Formulas PDF §3, §4.
 */

import type { TelemetryRow } from '@/types';
import type { StateTariff, ToDPeriod } from '@/tariffs';
import type { BillBreakdown, BillPeriodBreakdown } from '../types';
import { energyDelivered, loadFactor, safe, isValidPfReading, fifteenMinDemandBlocks } from '@/lib/calculations';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function weightedPF(rows: TelemetryRow[]): number {
  if (rows.length < 2) {
    const valid = rows.filter(isValidPfReading);
    if (!valid.length) return 0;
    return Math.min(valid.reduce((s, r) => s + safe(r.pfAvg), 0) / valid.length, 0.999);
  }
  const sorted   = [...rows].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  const deltaKwh  = Math.max(0, safe(sorted[sorted.length-1].energyWh)  - safe(sorted[0].energyWh))  / 1000;
  // energyVAh = raw VAh register (same unit as energyWh = raw Wh).
  // Divide by 1000 to get kVAh so PF = kWh / kVAh stays in (0, 1].
  // (Previous comment "stored as kVAh — do NOT divide" was wrong and produced PF ≈ 0.)
  const deltaKvah = Math.max(0, safe(sorted[sorted.length-1].energyVAh) - safe(sorted[0].energyVAh)) / 1000;
  if (deltaKvah > 0) return Math.min(deltaKwh / deltaKvah, 1);
  const valid = rows.filter(isValidPfReading);
  if (!valid.length) return 0;
  return Math.min(valid.reduce((s, r) => s + safe(r.pfAvg), 0) / valid.length, 0.999);
}

function periodRows(rows: TelemetryRow[], period: ToDPeriod): TelemetryRow[] {
  return rows.filter(r => r.todPeriod === period).sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
}

// ─── Engine ───────────────────────────────────────────────────────────────────

export function buildWestBengalBreakdown(
  rows:                      TelemetryRow[],
  tariff:                    StateTariff,
  daysPassed?:               number,
  daysTotal?:                number,
  contractDemandKvaOverride?: number,
): BillBreakdown {
  const CD    = contractDemandKvaOverride ?? tariff.defaultContractDemandKva;
  const minCD = CD * tariff.minDemandPct;
  const OFF_PEAK_MAX_KVA = CD * tariff.offPeakMaxDemandPct;

  const EMPTY: BillBreakdown = {
    spanHours: 0, monthFraction: 0,
    periods: [], ecTotalRs: 0, totalKvah: 0, totalKwh: 0,
    pfWeighted: 0, pfAdjTotalRs: 0,
    lfPct: 0, lfRebateRs: 0,
    peakKva: 0, mdKvaNormal: 0, mdKvaPeak: 0, mdKvaOffpeak: 0,
    chargeableKva: Math.round(minCD), dcRs: 0, dcPenalRs: 0, dcTotalRs: 0,
    netBeforeEdRs: 0, electricityDutyRs: 0, meterRentalRs: 0, customerChargesRs: 0,
    acdSurchargeRs: 0,
    grossPayableRs: 0, tprRs: 0, lpscRs: 0, netPayableRs: 0,
    rateTierLabel: tariff.getTierLabel(0, CD),
    // WB does not use TGSPDCL ToD model — always 0
    todPeakKvah: 0, todOffpeakKvah: 0,
    ecBaseRs: 0, todPeakPenaltyRs: 0, todOffpeakRebateRs: 0,
    normalKva: Math.round(minCD), addlKva: 0, dcNormalRs: 0, mdPenaltyRs: 0,
    subTotalRs: 0,
  };

  if (rows.length < 2) return EMPTY;

  const sorted       = [...rows].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  const spanHours    = (sorted[sorted.length-1].timestamp.getTime() - sorted[0].timestamp.getTime()) / 3_600_000;
  const monthFraction = spanHours / (30 * 24);
  const todPeriods: ToDPeriod[] = ['NORMAL', 'PEAK', 'OFF-PEAK'];
  const pfWtd = weightedPF(sorted);

  // ── Per-period energy & PF ────────────────────────────────────────────────
  const periods: BillPeriodBreakdown[] = todPeriods.map(p => {
    const pr            = periodRows(sorted, p);
    const kwh           = energyDelivered(pr);
    const rateRsKwh     = tariff.energyRates[p];
    const energyChargeRs = kwh * rateRsKwh;
    const pfPeriod      = weightedPF(pr) || pfWtd;
    const pfAdjPct      = tariff.pfAdjustmentPct(pfPeriod, p);
    const pfAdjRs       = energyChargeRs * (pfAdjPct / 100);
    return {
      period: p, energyConsumed: kwh, rateRsPerUnit: rateRsKwh, energyChargeRs,
      pfAdjPct, pfAdjRs,
      pfAvg: pfPeriod,   // per-period weighted PF — drives PF slab and PDF display
      kwh, kvah: 0, rateRsKwh, rateRsKvah: rateRsKwh,
    };
  });

  const ecTotalRs    = periods.reduce((s, p) => s + p.energyChargeRs, 0);
  const pfAdjTotalRs = -(periods.reduce((s, p) => s + p.pfAdjRs, 0));
  const totalKwh     = periods.reduce((s, p) => s + p.kwh, 0);

  // ── Load Factor ───────────────────────────────────────────────────────────
  const lfPct         = loadFactor(sorted);
  const lfRebateRs    = tariff.hasLfRebate ? -(tariff.lfRebateRsPerKwh(lfPct) * totalKwh) : 0;

  // ── Demand ────────────────────────────────────────────────────────────────
  // ── Max demand — PDF BE/BF/BG/BH ─────────────────────────────────────────
  // Classify each 15-min block by its START-time ToD period (not by split
  // accumulation inside a straddling window). This correctly implements the
  // PDF formula where "curr only takes [period] ToD time."
  const allBlocks = fifteenMinDemandBlocks(sorted);
  let mdKvaNormal  = 0;
  let mdKvaPeak    = 0;
  let mdKvaOffpeak = 0;
  for (const blk of allBlocks) {
    const blockHour   = new Date(blk.ts).getHours();
    const blockPeriod = tariff.todPeriodFor(blockHour);
    if      (blockPeriod === 'NORMAL')   { if (blk.kva > mdKvaNormal)  mdKvaNormal  = blk.kva; }
    else if (blockPeriod === 'PEAK')     { if (blk.kva > mdKvaPeak)    mdKvaPeak    = blk.kva; }
    else                                 { if (blk.kva > mdKvaOffpeak) mdKvaOffpeak = blk.kva; }
  }
  // PDF BH: max kVA = max(MD normal, MD peak, MD offpeak)
  const peakKva       = Math.max(mdKvaNormal, mdKvaPeak, mdKvaOffpeak);

  // PDF BI: Normal kVA = round(max(Max kVA, 0.85×CD))
  //   When MD > CD, chargeableKva = MD (not capped). Excess is separately
  //   charged in BJ/BL — there is no additional full-rate overrun penalty
  //   on top of BK+BL or it would double-count the excess kVA.
  const chargeableKva = Math.round(Math.max(peakKva, minCD));

  // BUG-4 FIX (demand — WBSEDCL B-IDIT): demand charge is a fixed monthly levy.
  // BK: Normal DC   = chargeableKva × 270 Rs/kVA   ← Normal Demand Charge
  // BL: Additional DC = addlKvaPeak × 135 Rs/kVA   ← Addl Demand Charge (separate line)
  // BM: Total DC     = BK + BL                     ← Total Demand Charge
  //
  // CRITICAL: BK and BL must be stored and returned separately so that:
  //   - The "Normal Demand Charge" display row shows BK only (chargeableKva × 270)
  //   - The "Additional Demand Charge" display row shows BL only (addlKvaPeak × 135)
  //   - Total DC = BK + BL (no double-counting)
  const addlKvaPeak   = Math.round(Math.max(0, mdKvaPeak - CD)); // PDF BJ
  const dcNormalRs    = chargeableKva * tariff.demandChargeRsKva;             // BK: Normal DC
  const dcAdditional  = addlKvaPeak   * (tariff.demandChargeRsKva / 2);      // BL: Addl DC
  const dcRs          = dcNormalRs + dcAdditional;                            // BM: Total DC

  // Off-peak ceiling overrun only (WBSEDCL: off-peak MD must not exceed 1.5×CD).
  // This is a separate tariff provision — not the same as Normal/Peak overrun.
  let mdPenaltyRs = 0;
  if (OFF_PEAK_MAX_KVA > 0 && mdKvaOffpeak > OFF_PEAK_MAX_KVA) {
    mdPenaltyRs += (mdKvaOffpeak - OFF_PEAK_MAX_KVA) * tariff.demandChargeRsKva;
  }

  // ── Bill assembly ─────────────────────────────────────────────────────────
  const netBeforeEdRs  = Math.max(0, ecTotalRs + pfAdjTotalRs + lfRebateRs + dcRs + mdPenaltyRs);
  const electricityDutyRs = netBeforeEdRs * tariff.electricityDutyValue; // pct-of-bill model
  // BUG-5 FIX: WBSEDCL charges a flat ₹1,200/month meter rental — never prorated.
  const meterRentalRs     = tariff.meterRentalRsMonth;
  const grossPayableRs    = netBeforeEdRs + electricityDutyRs + meterRentalRs;
  const tprRs             = -(grossPayableRs * tariff.tprFraction);
  const netPayableRs      = grossPayableRs + tprRs;

  // ── Projection ────────────────────────────────────────────────────────────
  let projectedEnergy: Record<ToDPeriod, number> | undefined;
  let projectedBillRs: number | undefined;

  if (daysPassed && daysPassed > 0 && daysTotal && daysTotal > daysPassed) {
    const rem = daysTotal - daysPassed;
    projectedEnergy = {} as Record<ToDPeriod, number>;
    let projEC = 0;
    for (const p of periods) {
      const projected = p.kwh + (p.kwh / daysPassed) * rem;
      projectedEnergy[p.period] = projected;
      const adjPct = tariff.pfAdjustmentPct(pfWtd, p.period);
      const ec     = projected * p.rateRsKwh;
      projEC += ec - ec * (adjPct / 100);
    }
    const projTotal   = Object.values(projectedEnergy).reduce((s, v) => s + v, 0);
    const projLF      = -(tariff.lfRebateRsPerKwh(lfPct) * projTotal);
    const projBefore  = Math.max(0, projEC + projLF + dcRs + mdPenaltyRs);
    const projED      = projBefore * tariff.electricityDutyValue;
    const projGross   = projBefore + projED + meterRentalRs;
    projectedBillRs   = projGross - projGross * tariff.tprFraction;
  }

  // PDF BJ: Additional kVA — exposed for display and PDF; uses MD_Peak only.
  const addlKva = addlKvaPeak;

  return {
    spanHours, monthFraction,
    periods, ecTotalRs, totalKvah: 0, totalKwh,
    pfWeighted: pfWtd, pfAdjTotalRs,
    lfPct, lfRebateRs,
    peakKva,                              // BH
    mdKvaNormal, mdKvaPeak, mdKvaOffpeak, // BE/BF/BG
    chargeableKva,
    dcRs,                                 // BM = BK + BL (Total DC)
    dcPenalRs: mdPenaltyRs,              // off-peak ceiling overrun only
    dcTotalRs: dcRs + mdPenaltyRs,
    netBeforeEdRs, electricityDutyRs, meterRentalRs, customerChargesRs: 0,
    acdSurchargeRs: 0,
    grossPayableRs, tprRs, lpscRs: 0, netPayableRs,
    rateTierLabel: tariff.getTierLabel(peakKva, CD),
    // WB does not use TGSPDCL ToD model — always 0
    todPeakKvah: 0, todOffpeakKvah: 0,
    ecBaseRs: 0, todPeakPenaltyRs: 0, todOffpeakRebateRs: 0,
    // WB projects kWh; the shared interface exposes one per-period projection
    // map (projectedKvah), so the kWh projection rides in it.
    projectedBillRs, projectedKvah: projectedEnergy,
    // Legacy aliases
    normalKva: chargeableKva,
    addlKva,
    dcNormalRs,       // BK: Normal Demand Charge = chargeableKva × 270
    mdPenaltyRs,      // off-peak ceiling overrun (separate clause)
    subTotalRs: netBeforeEdRs,
  };
}